import React, { useEffect, useMemo, useRef, useState } from "react";

import {
  Box,
  Collapse,
  IconButton,
  LinearProgress,
  Paper,
  Typography,
} from "@material-ui/core";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import CloseIcon from "@material-ui/icons/Close";

import { useGlobalTransfers } from "../../context/PhotoTransferContext";
import { makeStyles } from "@material-ui/core/styles";

import {
  PhotoStatus,
  PhotoSubStatus,
  PHOTO_TRANSFER_COPY,
  StatusMessages,
} from "../../utils/constants";

const useStyles = makeStyles((theme) => ({
  root: {
    position: "fixed",
    right: theme.spacing(2),
    bottom: theme.spacing(2),
    width: 360,
    maxWidth: "90vw",
    zIndex: theme.zIndex.modal + 10,
    boxShadow: theme.shadows[8],
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.paper,
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(1),
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
  headerActions: {
    display: "flex",
    alignItems: "center",
  },
  headerIconButton: {
    color: "inherit",
  },
  body: {
    maxHeight: theme.spacing(40),
    overflow: "auto",
  },
  item: {
    padding: theme.spacing(1),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  itemStatus: {
    color: theme.palette.text.secondary,
  },
  progress: {
    marginTop: theme.spacing(0.5),
  },
}));

const getStatusText = (photo) => {
  if (photo.statusMessage) return photo.statusMessage;
  if (photo.isPaused) return PHOTO_TRANSFER_COPY.STATUS_PAUSED;
  if (photo.status === PhotoStatus.UPLOADING) {
    if (photo.subStatus === PhotoSubStatus.UPLOADED)
      return StatusMessages.SAVING;
    if (photo.subStatus === PhotoSubStatus.RETRYING)
      return PHOTO_TRANSFER_COPY.STATUS_RETRYING;
    if (photo.subStatus === PhotoSubStatus.PAUSED)
      return StatusMessages.PAUSED_OFFLINE;
    if (photo.progress) {
      return StatusMessages.UPLOADING(Math.round(photo.progress * 100));
    }
    return PHOTO_TRANSFER_COPY.STATUS_UPLOADING;
  }
  if (photo.status === PhotoStatus.ERROR) {
    return StatusMessages.ERROR_GENERIC(
      photo.error || PHOTO_TRANSFER_COPY.STATUS_ERROR_UNKNOWN
    );
  }
  return PHOTO_TRANSFER_COPY.STATUS_PROCESSING;
};

const PhotoTransferPanelContent = () => {
  const transferState = useGlobalTransfers();
  const classes = useStyles();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isClosed, setIsClosed] = useState(false);
  const prevActiveCountRef = useRef(0);

  const activeItems = useMemo(() => {
    if (!transferState?.photos) return [];
    return Array.from(transferState.photos.values()).filter(
      (photo) => photo.status && photo.status !== PhotoStatus.DONE
    );
  }, [transferState?.photos]);

  useEffect(() => {
    const prevCount = prevActiveCountRef.current;
    if (prevCount === 0 && activeItems.length > 0) {
      setIsClosed(false);
    }
    prevActiveCountRef.current = activeItems.length;
  }, [activeItems.length]);

  const getParentLabel = (photo) => {
    // First, check if parentLabel is stored directly on the photo
    if (photo?.parentLabel) return photo.parentLabel;

    // Fallback to parentMetadata lookup
    if (!photo?.parentId) return null;
    const metadataSource = transferState?.parentMetadata;
    if (!metadataSource) return null;

    if (typeof metadataSource.get === "function") {
      return metadataSource.get(photo.parentId)?.label || null;
    }

    if (
      typeof metadataSource === "object" &&
      Object.prototype.hasOwnProperty.call(metadataSource, photo.parentId)
    ) {
      return metadataSource[photo.parentId]?.label || null;
    }

    return null;
  };

  if (isClosed || activeItems.length === 0) {
    return null;
  }

  return (
    <Paper elevation={6} className={classes.root}>
      <Box className={classes.header}>
        <Typography variant="subtitle2">
          {PHOTO_TRANSFER_COPY.PANEL_TITLE} ({activeItems.length})
        </Typography>
        <Box className={classes.headerActions}>
          <IconButton
            size="small"
            className={classes.headerIconButton}
            onClick={() => setIsCollapsed((prev) => !prev)}
          >
            {isCollapsed ? <ExpandMoreIcon /> : <ExpandLessIcon />}
          </IconButton>
          <IconButton
            size="small"
            className={classes.headerIconButton}
            onClick={() => setIsClosed(true)}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>
      <Collapse in={!isCollapsed}>
        <Box className={classes.body}>
          {activeItems.map((photo) => {
            const parentLabel = getParentLabel(photo);
            const shouldShowFileName =
              parentLabel && photo.name && parentLabel !== photo.name;

            return (
              <Box key={photo.key} className={classes.item}>
                <Typography variant="subtitle2" noWrap>
                  {parentLabel || photo.name || photo.key}
                </Typography>
                {shouldShowFileName && (
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    display="block"
                    noWrap
                  >
                    {photo.name}
                  </Typography>
                )}
                <Typography
                  variant="caption"
                  display="block"
                  className={classes.itemStatus}
                >
                  {getStatusText(photo)}
                </Typography>
                {photo.status === PhotoStatus.UPLOADING && (
                  <LinearProgress
                    variant={photo.progress ? "determinate" : "indeterminate"}
                    value={(photo.progress || 0) * 100}
                    className={classes.progress}
                  />
                )}
              </Box>
            );
          })}
        </Box>
      </Collapse>
    </Paper>
  );
};

const PhotoTransferPanel = () => <PhotoTransferPanelContent />;

export default PhotoTransferPanel;
